import React, { useState, useMemo } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContextAWS';
import { computeStandings, buildMatchupsWithResults } from '../../utils/standingsUtils';
import { TrophyIcon } from '../../components/icons/StreamlineFlexSolid';
import TeamRow from '../../components/TeamRow';
import { useTestDataBanner } from '../../contexts/TestDataBannerContext';

function LeagueStandings() {
  const { league } = useOutletContext();
  // Season-phase test-data mode: pre-draft has no games played, so records are 0.
  const { mode: phase } = useTestDataBanner();
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.userId || user?.id || user?.sub;
  const [sortBy, setSortBy] = useState('rank');

  const isUserId = (str) => {
    if (!str || typeof str !== 'string') return false;
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str) || (str.length > 20 && !str.includes('@')) || str.includes(':');
  };

  const getOwnerDisplayName = (team) => {
    let displayName = team.ownerName;
    if (!displayName || isUserId(displayName)) {
      displayName = team.ownerEmail ? team.ownerEmail.split('@')[0] : 'Owner';
    }
    if (isUserId(displayName)) {
      displayName = team.ownerEmail?.split('@')[0] || 'Owner';
    }
    return displayName;
  };

  const getTeamName = (team, index) => {
    if (team.name && String(team.name).trim() !== '') return team.name;
    return `Team ${index + 1}`;
  };

  const { isHeadToHead, isRotisserie, isPointsBased, standings: rawStandings, categories } = useMemo(
    () => (league ? computeStandings(league) : { standings: [], categories: [], isHeadToHead: false, isRotisserie: true, isPointsBased: false }),
    [league]
  );

  const getTeamPlacements = useMemo(() => {
    const leagueSettings = typeof league?.settings === 'string'
      ? JSON.parse(league?.settings || '{}')
      : (league?.settings || {});
    const history = leagueSettings.history || [];

    const matches = (teamOwner, teamName, seasonOwner, seasonTeamName) => {
      const ownerMatch = seasonOwner && (teamOwner === seasonOwner || String(teamOwner || '').toLowerCase() === String(seasonOwner).toLowerCase());
      const nameMatch = seasonTeamName && teamName && String(teamName).toLowerCase() === String(seasonTeamName).toLowerCase();
      return ownerMatch || nameMatch;
    };

    return (team) => {
      const owner = team.owner || team.ownerName || team.ownerEmail;
      const teamName = team.name || '';
      const placements = [];
      history.forEach((season) => {
        if (matches(owner, teamName, season.champion, season.championTeamName)) placements.push({ place: 1, year: season.year });
        else if (matches(owner, teamName, season.runnerUp, season.runnerUpTeamName)) placements.push({ place: 2, year: season.year });
        else if (matches(owner, teamName, season.thirdPlace, season.thirdPlaceTeamName)) placements.push({ place: 3, year: season.year });
      });
      return placements;
    };
  }, [league]);

  const standings = useMemo(() => {
    const list = (rawStandings || []).map((team, index) => ({
      ...team,
      // Pre-draft: the season hasn't started, so every record is empty.
      ...(phase === 'pre-draft' ? { wins: 0, losses: 0, ties: 0, rotoPoints: 0, points: 0, categoryValues: {} } : null),
      name: getTeamName(team, index),
      displayOwnerName: getOwnerDisplayName(team),
      _originalRank: index + 1,
    }));
    if (!sortBy) return list;
    const sorted = [...list];
    if (sortBy === 'rank') return list;
    if (sortBy === 'team') {
      sorted.sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
      return sorted;
    }
    if (isHeadToHead) {
      if (sortBy === 'wins') sorted.sort((a, b) => (b.wins - a.wins) || (a.losses - b.losses) || ((b.ties || 0) - (a.ties || 0)));
      else if (sortBy === 'losses') sorted.sort((a, b) => (a.losses - b.losses) || (b.wins - a.wins));
      else if (sortBy === 'ties') sorted.sort((a, b) => (b.ties || 0) - (a.ties || 0));
      else return list;
    } else if (isRotisserie) {
      if (sortBy === 'rotoPoints') sorted.sort((a, b) => (b.rotoPoints ?? 0) - (a.rotoPoints ?? 0));
      else if (categories?.some((c) => c.key === sortBy)) {
        const cat = categories.find((c) => c.key === sortBy);
        const higherBetter = cat?.higherIsBetter !== false;
        sorted.sort((a, b) => {
          const va = parseFloat(a.categoryValues?.[sortBy]) || 0;
          const vb = parseFloat(b.categoryValues?.[sortBy]) || 0;
          return higherBetter ? vb - va : va - vb;
        });
      }
      else return list;
    } else if (isPointsBased) {
      if (sortBy === 'points') sorted.sort((a, b) => (b.points ?? 0) - (a.points ?? 0));
      else return list;
    }
    return sorted;
  }, [rawStandings, sortBy, isHeadToHead, isRotisserie, isPointsBased, categories, phase]);

  const placeColors = { 1: '#d4af37', 2: '#c0c0c0', 3: '#cd7f32' }; // gold, silver, bronze
  const placeLabels = { 1: '1st', 2: '2nd', 3: '3rd' };

  // Derive each team's current streak (e.g. "W4"/"L2") from the underlying weekly
  // matchup results. Only meaningful for head-to-head leagues, which are the only
  // scoring type with a per-week win/loss/tie result to build a streak from.
  const streaksByTeamId = useMemo(() => {
    if (phase === 'pre-draft') return {}; // no games played yet
    if (!isHeadToHead || !league) return {};
    const matchups = buildMatchupsWithResults(league);
    const gamesByTeam = {};
    matchups.forEach((m) => {
      const w1 = m.team1FullWins ?? 0;
      const w2 = m.team2FullWins ?? 0;
      const r1 = w1 > w2 ? 'W' : w2 > w1 ? 'L' : 'T';
      const r2 = w2 > w1 ? 'W' : w1 > w2 ? 'L' : 'T';
      if (!gamesByTeam[m.team1Id]) gamesByTeam[m.team1Id] = [];
      if (!gamesByTeam[m.team2Id]) gamesByTeam[m.team2Id] = [];
      gamesByTeam[m.team1Id].push({ week: m.week, result: r1 });
      gamesByTeam[m.team2Id].push({ week: m.week, result: r2 });
    });
    const streaks = {};
    Object.keys(gamesByTeam).forEach((teamId) => {
      const games = [...gamesByTeam[teamId]].sort((a, b) => a.week - b.week);
      let type = null;
      let count = 0;
      for (let i = games.length - 1; i >= 0; i -= 1) {
        const { result } = games[i];
        if (result === 'T') break;
        if (type === null) {
          type = result;
          count = 1;
        } else if (result === type) {
          count += 1;
        } else {
          break;
        }
      }
      if (type && count > 0) streaks[teamId] = `${type}${count}`;
    });
    return streaks;
  }, [league, isHeadToHead, phase]);

  const handleSort = (key) => {
    if (sortBy === key) return;
    setSortBy(key);
  };

  const isRotisserieLeague = isRotisserie && !isHeadToHead;
  const playoffsEnabled = !isRotisserieLeague && (league?.season?.playoffsEnabled !== false) && (league?.settings?.schedule?.playoffStartWeek || league?.settings?.schedule?.playoffTeams);
  const playoffTeams = parseInt(league?.settings?.schedule?.playoffTeams || '4', 10);
  const numCols = 2 + (isHeadToHead ? 3 : 1) + (!isHeadToHead ? Math.min(12, categories?.length || 0) : 0);

  return (
    <div>
      <div className="card roster-style-table-wrap">
        <div>
          <table className="apple-table">
            <thead>
              <tr>
                <th
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('rank')}
                  title="Sort by Rank"
                >
                  Rank {sortBy === 'rank' && '▼'}
                </th>
                <th
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('team')}
                  title="Sort by Team Name"
                >
                  Team {sortBy === 'team' && '▼'}
                </th>
                {isHeadToHead && (
                  <>
                    <th
                      style={{ textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('wins')}
                      title="Sort by Wins"
                    >
                      W {sortBy === 'wins' && '▼'}
                    </th>
                    <th
                      style={{ textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('losses')}
                      title="Sort by Losses"
                    >
                      L {sortBy === 'losses' && '▼'}
                    </th>
                    <th
                      style={{ textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('ties')}
                      title="Sort by Ties"
                    >
                      T {sortBy === 'ties' && '▼'}
                    </th>
                  </>
                )}
                {isRotisserie && !isHeadToHead && (
                  <th
                    style={{ textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('rotoPoints')}
                    title="Sort by Roto Points"
                  >
                    Roto Pts {sortBy === 'rotoPoints' && '▼'}
                  </th>
                )}
                {isPointsBased && !isHeadToHead && (
                  <th
                    style={{ textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('points')}
                    title="Sort by Points"
                  >
                    Points {sortBy === 'points' && '▼'}
                  </th>
                )}
                {!isHeadToHead && (categories || []).slice(0, 12).map((cat) => (
                  <th
                    key={cat.key}
                    style={{ textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort(cat.key)}
                    title={`Sort by ${cat.label || cat.key}`}
                  >
                    {cat.label || cat.key} {sortBy === cat.key && '▼'}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {standings.map((team, index) => {
                const isMyTeam = Boolean(userId && team.owner === userId);
                const showPlayoffBreak = playoffsEnabled && playoffTeams > 0 && (team._originalRank ?? index + 1) === playoffTeams + 1;
                return (
                  <React.Fragment key={team.id || team.teamId || index}>
                    {showPlayoffBreak && (
                      <tr>
                        <td
                          colSpan={numCols}
                          style={{
                            padding: '6px 8px',
                            backgroundColor: 'var(--surface-muted)',
                            borderTop: '2px solid var(--warning)',
                            borderBottom: '2px solid var(--warning)',
                            textAlign: 'center',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: 'var(--muted)',
                          }}
                        >
                          — Playoffs —
                        </td>
                      </tr>
                    )}
                    <tr
                    style={isMyTeam ? {
                      backgroundColor: 'rgba(0, 113, 227, 0.08)',
                      borderLeft: '4px solid var(--primary)',
                      fontWeight: '600',
                    } : undefined}
                  >
                    <td colSpan={2} style={{ padding: '4px 8px' }}>
                      <TeamRow
                        team={{ ...team, ownerName: team.displayOwnerName }}
                        rank={team._originalRank ?? index + 1}
                        streak={streaksByTeamId[team.id || team.teamId]}
                        showRecord={false}
                        onClick={() => {
                          const teamId = team.id || team.teamId;
                          const leagueId = league?.id || league?.leagueId;
                          if (teamId && leagueId) navigate(`/league/${leagueId}/team/${teamId}/roster`);
                        }}
                      />
                      {getTeamPlacements(team).length > 0 && (
                        <div style={{ display: 'flex', gap: '4px', marginTop: '4px', paddingLeft: '4px' }}>
                          {getTeamPlacements(team).map((p) => (
                            <span
                              key={`${p.place}-${p.year}`}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: '20px',
                                minHeight: '20px',
                                padding: '2px 4px',
                                cursor: 'default',
                                lineHeight: 1,
                                borderRadius: '4px',
                                position: 'relative',
                                zIndex: 1,
                                color: placeColors[p.place] || 'var(--muted)',
                              }}
                              title={`${p.year} season – ${placeLabels[p.place]} place`}
                            >
                              <TrophyIcon size={16} />
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    {isHeadToHead && (
                      <>
                        <td style={{ padding: '8px', textAlign: 'center', fontWeight: 'bold' }}>
                          {team.wins ?? 0}
                        </td>
                        <td style={{ padding: '8px', textAlign: 'center', fontWeight: 'bold' }}>
                          {team.losses ?? 0}
                        </td>
                        <td style={{ padding: '8px', textAlign: 'center', fontWeight: 'bold' }}>
                          {team.ties ?? 0}
                        </td>
                      </>
                    )}
                    {isRotisserie && !isHeadToHead && (
                      <td style={{ padding: '8px', textAlign: 'center', fontWeight: 'bold', color: 'var(--primary)' }}>
                        {team.rotoPoints ?? '—'}
                      </td>
                    )}
                    {isPointsBased && !isHeadToHead && (
                      <td style={{ padding: '8px', textAlign: 'center', fontWeight: 'bold', color: 'var(--primary)' }}>
                        {team.points ?? '—'}
                      </td>
                    )}
                    {!isHeadToHead && (categories || []).slice(0, 12).map((cat) => (
                      <td key={cat.key} style={{ padding: '8px', textAlign: 'center' }}>
                        {team.categoryValues?.[cat.key] ?? '—'}
                      </td>
                    ))}
                  </tr>
                </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}

export default LeagueStandings;
